import axios from "axios";
import { useMutation, useQueryClient } from "react-query";
import TTaxGroup from "../../types/TTaxGroup";

type TProps = {
    groupName: string;
    setGroupName: (groupName: string) => void;
    taxes: any[];
    selectedTaxes: number[];
    handleTaxSelection: (taxId: number) => void;
    handleSaveGroup: () => void;
    isEditMode: boolean;
    setIsGroupModalOpen: (isOpen: boolean) => void;
    selectedGroup: TTaxGroup | null;
    setSelectedGroup: (group: TTaxGroup | null) => void;
};

const GroupModal = (
    {
        groupName,
        setGroupName,
        taxes,
        selectedTaxes,
        handleTaxSelection,
        handleSaveGroup,
        isEditMode,
        setIsGroupModalOpen,
        selectedGroup,
        setSelectedGroup
    }: TProps
) => {
    const queryClient = useQueryClient();
    const archiveTaxGroup = useMutation(
        async (group: { id: number; archived: boolean }) => {


            const response = await axios.put(
                `${process.env.REACT_APP_SERVER_URL}/api/v1/company/tax_groups/archive/${group.id}`,
                { archived: !group.archived } // Toggle archive state
            );


            return { id: group.id, archived: !group.archived };
        },
        {
            onMutate: async (updatedGroup) => {


                await queryClient.cancelQueries("taxes");

                // Snapshot current data
                const previousGroups = queryClient.getQueryData<TTaxGroup[]>("taxes");

                if (!Array.isArray(previousGroups)) {
                    console.warn("previousGroups is not an array. Skipping optimistic update.");
                    return { previousGroups };
                }

                // Optimistically update UI
                queryClient.setQueryData<TTaxGroup[]>("taxes", (oldGroups = []) =>
                    oldGroups.map((group) =>
                        group.id === updatedGroup.id ? { ...group, archived: !updatedGroup.archived } : group
                    )
                );

                return { previousGroups };
            },
            onSuccess: (updatedGroup) => {


                if (selectedGroup && selectedGroup.id === updatedGroup.id) {
                    setSelectedGroup({ ...selectedGroup, archived: updatedGroup.archived });
                }

                queryClient.invalidateQueries("taxes");
            },
            onError: (err, newGroup, context) => {
                console.error("Error archiving tax group:", err);
                if (context?.previousGroups) {
                    queryClient.setQueryData("taxes", context.previousGroups);
                }
            },
            onSettled: () => {

                queryClient.invalidateQueries("taxes");
            },
        }
    );

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
                <h3 className="text-lg font-semibold mb-4">
                    {isEditMode ? "Edit Tax Group" : "Create New Tax Group"}
                </h3>
                <input
                    type="text"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    placeholder="Group Name"
                    className="w-full mb-4 p-2 border border-gray-300 rounded"
                />
                <div className="mb-4">
                    <h4 className="font-medium mb-2">Select Taxes</h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                        {taxes.map((tax) => (
                            <label key={tax.id} className="flex items-center space-x-3">
                                <input
                                    type="checkbox"
                                    checked={selectedTaxes.includes(Number(tax.id))}
                                    onChange={() => handleTaxSelection(tax.id)}
                                    className="form-checkbox h-5 w-5 text-blue-600"
                                />
                                <span>{tax.label}</span>
                            </label>
                        ))}
                    </div>
                </div>
                <div className="flex justify-end space-x-4">
                    <button
                        onClick={() => setIsGroupModalOpen(false)}
                        className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                    >
                        Cancel
                    </button>

                    {/* Archive Button - Only show if editing an existing group */}
                    {isEditMode && selectedGroup && (
                        <button
                            onClick={() =>
                                archiveTaxGroup.mutate({ id: selectedGroup.id!, archived: selectedGroup.archived })
                            }
                            className={`px-4 py-2 rounded transition ${selectedGroup.archived ? "bg-green-500 hover:bg-green-600" : "bg-red-500 hover:bg-red-600"
                                } text-white`}
                            disabled={archiveTaxGroup.isLoading}
                        >
                            {archiveTaxGroup.isLoading ? "Updating..." : selectedGroup.archived ? "Unarchive" : "Archive"}
                        </button>
                    )}

                    <button
                        onClick={handleSaveGroup}
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                        {isEditMode ? "Update Group" : "Create Group"}
                    </button>
                </div>
            </div>
        </div>
    )
};

export default GroupModal;